/**
 * APEX v7.8.9 — Game-level Insights : Patterns POSITIFS enrichis
 *
 * Objectif : ne pas que pointer les erreurs. Mettre en valeur ce que le
 * joueur a bien fait, pour renforcer les bons comportements et garder
 * la motivation. Comparaisons à TA baseline quand dispo.
 */

function isCancelled(game) {
  if (!game) return false;
  if (game.isRemake === true) return true;
  if (typeof game.duration === 'number' && game.duration > 0 && game.duration < 5) return true;
  return false;
}

function computeKda(g) {
  const d = g.deaths || 0;
  if (d === 0) return ((g.kills || 0) + (g.assists || 0)) * 1.5;
  return ((g.kills || 0) + (g.assists || 0)) / d;
}

export function analyzePositives(game, context = {}) {
  if (!game || isCancelled(game)) return [];
  const out = [];
  const stats = context?.userStats || {};

  // ── CS/min remarquable ──
  const cs = game.csPerMin || 0;
  if (cs >= 9) {
    out.push({
      id: 'P_high_cs',
      category: 'P',
      severity: 'positive',
      title: 'Farm excellent',
      short: `${cs.toFixed(1)} CS/min`,
      long: `${cs.toFixed(1)} CS/min, c'est un très bon niveau de farm. Ta priorité aux minions paie — continue à ne pas lâcher les vagues même en rotation.`
    });
  } else if (typeof stats.avgCsPerMin === 'number' && cs > stats.avgCsPerMin * 1.15 && cs > 0) {
    out.push({
      id: 'P_cs_above_baseline',
      category: 'P',
      severity: 'positive',
      title: 'Farm au-dessus de ta moyenne',
      short: `${cs.toFixed(1)} CS/min (moy. ${stats.avgCsPerMin.toFixed(1)})`,
      long: `Tu as farmé mieux que ta moyenne habituelle cette game. Le genre de constance à reproduire.`
    });
  }

  // ── KDA solide ──
  const kda = computeKda(game);
  if (game.deaths === 0 && (game.kills + game.assists) >= 5) {
    out.push({
      id: 'P_deathless_impact',
      category: 'P',
      severity: 'positive',
      title: 'Game parfaite côté survie',
      short: `0 mort, ${game.kills}/${game.assists} K/A`,
      long: `Zéro mort tout en pesant sur la game (${game.kills} kills, ${game.assists} assists). Positionnement irréprochable — c'est LE standard ADC.`
    });
  } else if (kda >= 4) {
    out.push({
      id: 'P_high_kda',
      category: 'P',
      severity: 'positive',
      title: 'KDA dominant',
      short: `KDA ${kda.toFixed(1)}`,
      long: `KDA de ${kda.toFixed(1)} : tu as eu de l'impact en limitant tes morts. Exactement le profil qui fait gagner des games en SoloQ.`
    });
  }

  // ── Faible nombre de morts ──
  const deaths = game.deaths ?? (game.deathDetails?.length || 0);
  if (deaths > 0 && deaths <= 3 && (game.duration || 0) >= 25) {
    out.push({
      id: 'P_low_deaths_long_game',
      category: 'P',
      severity: 'positive',
      title: 'Discipline sur la durée',
      short: `${deaths} mort${deaths > 1 ? 's' : ''} sur ${game.duration}min`,
      long: `Seulement ${deaths} mort${deaths > 1 ? 's' : ''} sur une game longue (${game.duration}min). Tu as su rester patient et ne pas forcer — la marque d'un ADC mature.`
    });
  }

  // ── Lane gagnée à 15 ──
  const me = game.timeFrames?.min15?.my;
  const opp = game.timeFrames?.min15?.opp;
  if (me && opp) {
    const goldDiff = (me.gold || 0) - (opp.gold || 0);
    const csDiff = (me.cs || 0) - (opp.cs || 0);
    if (goldDiff >= 800 || csDiff >= 20) {
      out.push({
        id: 'P_lane_dominated',
        category: 'P',
        severity: 'positive',
        title: 'Lane dominée à 15min',
        short: goldDiff >= 800 ? `+${Math.round(goldDiff)} gold @15` : `+${csDiff} CS @15`,
        long: `Tu as pris un vrai ascendant en lane (${goldDiff >= 0 ? '+' : ''}${Math.round(goldDiff)} gold, ${csDiff >= 0 ? '+' : ''}${csDiff} CS à 15min). Cette avance est ta fenêtre pour prendre des objectifs — pense à la convertir.`
      });
    }
  }

  // ── Dégâts élevés ──
  const dmg = game.damageDealt || 0;
  const dur = game.duration || 0;
  if (dur > 0) {
    const dpm = dmg / dur;
    if (dpm >= 900) {
      out.push({
        id: 'P_high_dpm',
        category: 'P',
        severity: 'positive',
        title: 'Dégâts énormes',
        short: `${Math.round(dpm)} dmg/min`,
        long: `${Math.round(dpm)} dégâts/min aux champions : tu as été une vraie menace en teamfight. Bon positionnement de carry.`
      });
    }
  }

  // ── Vision (rare pour un ADC, à valoriser) ──
  const vision = game.myStats?.visionScore ?? 0;
  if (vision >= 40 && (game.duration || 0) >= 25) {
    out.push({
      id: 'P_good_vision',
      category: 'P',
      severity: 'positive',
      title: 'Vision exemplaire',
      short: `Vision score ${Math.round(vision)}`,
      long: `Vision score de ${Math.round(vision)} pour un ADC, c'est au-dessus de la moyenne. Contribuer à la vision réduit les morts en rotation — continue.`
    });
  }

  // ── Victoire en remontée (perdu lane mais win) ──
  if (game.result === 'W' && me && opp) {
    const goldDiff = (me.gold || 0) - (opp.gold || 0);
    if (goldDiff <= -800) {
      out.push({
        id: 'P_comeback_win',
        category: 'P',
        severity: 'positive',
        title: 'Victoire en remontée',
        short: 'Lane perdue mais game gagnée',
        long: `Tu étais derrière en lane à 15min (${Math.round(goldDiff)} gold) et la game est gagnée. Bravo pour le mental et la capacité à revenir — ne pas tilt sur un mauvais début, c'est ce qui fait climb.`
      });
    }
  }

  return out;
}
